import { useCallback, useState } from "react";

import { useMutation, useQueryClient } from "react-query";
import { ShortlistClient } from "services/motorpoint/user-context-api";
import type {
    Shortlist,
    StockNumber,
    UpdateListParams,
    UserContext,
    UserContextId,
} from "services/motorpoint/user-context-api/types";

import { QUERY_KEY } from "./useShortlist";

const shortlistClient = new ShortlistClient({
    useEnv: true,
});

export const useShortlistApi = ({ userContextId }: UserContextId) => {
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const onSuccess = (data: Shortlist) => {
        queryClient.setQueryData(QUERY_KEY, data);

        queryClient.setQueryData(
            ["userContext", userContextId],
            (oldData: UserContext | undefined) => {
                return {
                    ...oldData,
                    savedDataMeta: {
                        ...oldData?.savedDataMeta,
                        shortlistCount: data.stockNumbers.length || 0,
                    },
                } as UserContext;
            }
        );
    };

    const removeFromListBind = shortlistClient.removeFromList.bind(shortlistClient);
    const addToListBind = shortlistClient.addToList.bind(shortlistClient);
    const removeAllBind = shortlistClient.removeAll.bind(shortlistClient);

    const removeFromListMutation = useMutation({ mutationFn: removeFromListBind, onSuccess });
    const addToListMutation = useMutation({ mutationFn: addToListBind, onSuccess });
    const removeAllMutation = useMutation({ mutationFn: removeAllBind, onSuccess });

    const removeFromList = useCallback(
        async (stockNumber: StockNumber) => {
            try {
                const params: UpdateListParams = {
                    userContextId,
                    stockNumber,
                };
                const data = await removeFromListMutation.mutateAsync(params);

                return data;
            } catch (error) {
                setError("Could not remove item from shortlist");
            }
        },
        // eslint-disable-next-line
        [removeFromListMutation.mutateAsync]
    );

    const addToList = useCallback(
        async (stockNumber: StockNumber) => {
            try {
                const params: UpdateListParams = {
                    userContextId,
                    stockNumber,
                };

                const data = await addToListMutation.mutateAsync(params);

                return data;
            } catch (error) {
                setError("Could not add to shortlist");
            }
        },
        // eslint-disable-next-line
        [addToListMutation.mutateAsync]
    );

    const removeAll = useCallback(
        async () => {
            try {
                const data = await removeAllMutation.mutateAsync({ userContextId });

                return data;
            } catch (error) {
                setError("Could not remove all from shortlist");
            }
        },
        // eslint-disable-next-line
        [removeAllMutation.mutateAsync]
    );

    return {
        error,
        addToList,
        removeFromList,
        removeAll,
    };
};
